import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import {
  Clapperboard, Download, Link2, Trash2, Globe, Eye, RefreshCw, Loader2, Search, ExternalLink, Film,
} from "lucide-react";

function fmt(sec) {
  if (sec == null) return "—";
  const s = Math.round(sec);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function StatusBadge({ status }) {
  const map = {
    queued: ["Queued", "bg-amber-500/15 text-amber-500 border-amber-500/30"],
    processing: ["Rendering", "bg-sky-500/15 text-sky-400 border-sky-500/30"],
    ready: ["Ready", "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"],
    failed: ["Failed", "bg-rose-500/15 text-rose-500 border-rose-500/30"],
  };
  const [txt, cls] = map[status] || ["—", "bg-muted text-muted-foreground"];
  return <Badge variant="outline" className={`rounded-sm text-[9px] font-mono uppercase tracking-wider ${cls}`}>{txt}</Badge>;
}

const ASPECT_WRAP = {
  "9:16": "aspect-[9/16] max-w-[150px]",
  "1:1": "aspect-square max-w-[200px]",
  "16:9": "aspect-video",
};

export default function Clips() {
  const [clips, setClips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/clips");
      setClips(r.data.items || []);
    } catch { toast.error("Failed to load clips"); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll while any clip is rendering.
  useEffect(() => {
    clearInterval(pollRef.current);
    if (clips.some((c) => c.status === "queued" || c.status === "processing")) {
      pollRef.current = setInterval(load, 3500);
    }
    return () => clearInterval(pollRef.current);
  }, [clips, load]);

  const togglePublic = async (clip) => {
    try {
      const r = await api.patch(`/clips/${clip.id}`, { public: !clip.public });
      setClips((p) => p.map((c) => (c.id === clip.id ? r.data : c)));
      toast.success(r.data.public ? "Clip is now public" : "Clip made private");
    } catch { toast.error("Update failed"); }
  };

  const removeClip = async (clip) => {
    if (!window.confirm("Delete this clip? This removes the file from storage.")) return;
    try {
      await api.delete(`/clips/${clip.id}`);
      setClips((p) => p.filter((c) => c.id !== clip.id));
      toast.success("Clip deleted");
    } catch { toast.error("Delete failed"); }
  };

  const copyLink = async (clip) => {
    try { await navigator.clipboard.writeText(`${window.location.origin}/api/public/clip/${clip.id}/share`); toast.success("Share link copied"); }
    catch { toast.error("Copy failed"); }
  };

  const filtered = clips.filter((c) => {
    if (!q.trim()) return true;
    const hay = `${c.title || ""} ${c.caption || ""} ${c.asset_name || ""}`.toLowerCase();
    return hay.includes(q.trim().toLowerCase());
  });

  const readyCount = clips.filter((c) => c.status === "ready").length;
  const totalViews = clips.reduce((n, c) => n + (c.views || 0), 0);

  return (
    <div className="space-y-6" data-testid="clips-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-muted-foreground">↳ Social Clips</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold flex items-center gap-3">
            <Clapperboard className="w-9 h-9 text-fuchsia-400" strokeWidth={1.5} /> Clips
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {clips.length} clip{clips.length === 1 ? "" : "s"} · {readyCount} ready · {totalViews} total view{totalViews === 1 ? "" : "s"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clips" className="rounded-sm pl-8 h-9 text-xs w-48" data-testid="clips-search" />
          </div>
          <Button variant="outline" onClick={load} className="rounded-sm text-xs border-border" data-testid="clips-refresh">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm py-20 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading clips…</div>
      ) : filtered.length === 0 ? (
        <div className="panel rounded-md py-20 text-center" data-testid="clips-empty">
          <Film className="w-10 h-10 mx-auto text-muted-foreground/50" strokeWidth={1.25} />
          <div className="font-display font-bold mt-3">No clips yet</div>
          <p className="text-sm text-muted-foreground mt-1">Open any asset in the Asset Library and hit <b>Clips</b> to cut your first social clip.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map((c) => (
            <div key={c.id} className="panel rounded-md overflow-hidden flex flex-col group" data-testid={`clip-card-${c.id}`}>
              <div className="relative bg-black/50 flex items-center justify-center overflow-hidden">
                <div className={`w-full ${ASPECT_WRAP[c.aspect] || "aspect-video"} mx-auto flex items-center justify-center`}>
                  {c.poster_url ? (
                    <img src={c.poster_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Clapperboard className="w-7 h-7 text-muted-foreground/60" />
                  )}
                </div>
                <div className="absolute top-2 left-2 flex items-center gap-1.5">
                  <StatusBadge status={c.status} />
                  <Badge variant="outline" className="rounded-sm text-[9px] font-mono bg-black/50 border-white/20 text-white/80">{c.aspect}</Badge>
                </div>
                {c.public && (
                  <Badge variant="outline" className="absolute top-2 right-2 rounded-sm text-[9px] font-mono bg-emerald-500/20 border-emerald-500/40 text-emerald-300 flex items-center gap-1"><Globe className="w-2.5 h-2.5" /> Public</Badge>
                )}
                <div className="absolute bottom-2 right-2 flex items-center gap-1 text-[10px] font-mono text-white/80 bg-black/50 rounded-sm px-1.5 py-0.5">
                  <Eye className="w-2.5 h-2.5" /> {c.views || 0}
                </div>
              </div>

              <div className="p-3 flex flex-col flex-1">
                <div className="font-display font-bold text-sm truncate" title={c.title}>{c.title}</div>
                <div className="text-[10px] font-mono text-muted-foreground mt-0.5 truncate">{c.asset_name} · {fmt(c.duration_sec)}</div>
                {c.status === "failed" && <div className="text-[11px] text-rose-500 mt-1 line-clamp-2">{c.error}</div>}
                {(c.status === "queued" || c.status === "processing") && (
                  <div className="text-[11px] font-mono text-sky-400/80 mt-2 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> {c.status === "queued" ? "Waiting…" : "Rendering…"}</div>
                )}

                {c.status === "ready" && (
                  <>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <a href={c.video_url} download target="_blank" rel="noopener noreferrer" className="text-[11px] font-mono text-sky-400 hover:text-sky-300 inline-flex items-center gap-1" data-testid={`clip-download-${c.id}`}><Download className="w-3 h-3" /> Download</a>
                      <button onClick={() => copyLink(c)} className="text-[11px] font-mono text-fuchsia-400 hover:text-fuchsia-300 inline-flex items-center gap-1" data-testid={`clip-copylink-${c.id}`}><Link2 className="w-3 h-3" /> Link</button>
                      {c.public && (
                        <a href={`/clip/${c.id}`} target="_blank" rel="noopener noreferrer" className="text-[11px] font-mono text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1" data-testid={`clip-open-${c.id}`}><ExternalLink className="w-3 h-3" /> Open</a>
                      )}
                    </div>
                    <div className="flex items-center justify-between mt-3 pt-2 border-t border-border">
                      <label className="text-[11px] font-mono text-muted-foreground inline-flex items-center gap-1.5 cursor-pointer">
                        <Globe className="w-3 h-3" /> Public
                        <Switch checked={!!c.public} onCheckedChange={() => togglePublic(c)} data-testid={`clip-public-${c.id}`} />
                      </label>
                      <button onClick={() => removeClip(c)} className="text-muted-foreground hover:text-rose-500" data-testid={`clip-delete-${c.id}`} title="Delete clip">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </>
                )}
                {c.status !== "ready" && (
                  <div className="mt-auto pt-2 flex justify-end">
                    <button onClick={() => removeClip(c)} className="text-muted-foreground hover:text-rose-500" data-testid={`clip-delete-${c.id}`} title="Delete clip">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
